/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import StyledThemingExample from './styled-theming-example';
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    a: "a",
    h3: "h3",
    pre: "pre",
    code: "code",
    h4: "h4"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Styled Theming"
    }), "\n", _jsxs(_components.p, {
      children: ["Create themes for your styled components using\n", _jsx(_components.a, {
        href: "https://github.com/styled-components/styled-theming",
        children: "styled-theming"
      })]
    }), "\n", _jsx(StyledThemingExample, {}), "\n", _jsxs(_components.p, {
      children: ["Read the ", _jsx(_components.a, {
        href: "http://thejameskyle.com/styled-theming.html",
        children: "introductory blog post"
      })]
    }), "\n", _jsx(_components.h3, {
      children: "Install"
    }), "\n", _jsx(_components.p, {
      children: "Install the babel-plugin first:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "npm install --save styled-theming\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Example"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import React from 'react'\nimport styled, { ThemeProvider } from 'styled-components'\nimport theme from 'styled-theming'\n\nconst boxBackgroundColor = theme('mode', {\n  light: '#fff',\n  dark: '#000',\n})\n\nconst Box = styled.div`\n  background-color: ${boxBackgroundColor};\n`\n\nexport default function App() {\n  return (\n    <ThemeProvider theme={{ mode: 'light' }}>\n      <Box>Hello World</Box>\n    </ThemeProvider>\n  )\n}\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "API"
    }), "\n", _jsx(_components.h4, {
      children: _jsx(_components.code, {
        children: "<ThemeProvider>"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["See ", _jsx(_components.a, {
        href: "https://www.styled-components.com/docs/advanced#theming",
        children: "styled-components docs"
      })]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " is part of styled-components, but is required for\nstyled-theming."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { ThemeProvider } from 'styled-components'\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " accepts a single prop ", _jsx(_components.code, {
        children: "theme"
      }), " which you should pass an\nobject with either strings or getter functions. For example:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "<ThemeProvider theme={{ mode: \"dark\", size: \"large\" }}>\n<ThemeProvider theme={{ mode: modes => modes.dark, size: sizes => sizes.large }}>\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["You should generally set up a ", _jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " at the root of your app:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "function App() {\n  return (\n    <ThemeProvider theme={...}>\n      {/* rest of your app */}\n    </ThemeProvider>\n  );\n}\n"
      })
    }), "\n", _jsx(_components.h4, {
      children: _jsx(_components.code, {
        children: "theme(name, values)"
      })
    }), "\n", _jsx(_components.p, {
      children: "Most of your theming will be done with this function."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "name"
      }), " should match one of the keys in your ", _jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " theme."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: ";<ThemeProvider theme={{ whatever: '...' }} />\n"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "theme(\"whatever\", {...});\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "values"
      }), " should be an object where one of the keys will be selected by the\nvalue provided to ", _jsx(_components.code, {
        children: "<ThemeProvider>"
      }), " theme."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "<ThemeProvider theme={{ mode: \"light\" }} />\n<ThemeProvider theme={{ mode: \"dark\" }} />\n\ntheme(\"mode\", {\n  light: \"...\",\n  dark: \"...\",\n});\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "The values of this object can be any CSS value."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "theme(\"mode\", {\n  light: \"#fff\",\n  dark: \"#000\",\n});\n\ntheme(\"font\", {\n  sansSerif: '\"Helvetica Neue\", Helvetica, Arial, sans-serif',\n  serif: 'Georgia, Times, \"Times New Roman\", serif',\n  monoSpaced: \"Consolas, monaco, monospace\",\n});\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "These values can also be functions that return CSS values."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "theme('mode', {\n  light: props => props.theme.userProfileAccentColor.light,\n  dark: props => props.theme.userProfileAccentColor.dark,\n})\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "theme"
      }), " will create a function that you can use as a value in\nstyled-component's ", _jsx(_components.code, {
        children: "styled"
      }), " function."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import styled from 'styled-components'\nimport theme from 'styled-theming'\n\nconst backgroundColor = theme('mode', {\n  light: '#fff',\n  dark: '#000',\n})\n\nconst Box = styled.div`\n  background-color: ${backgroundColor};\n`\n"
      })
    }), "\n", _jsx(_components.h4, {
      children: _jsx(_components.code, {
        children: "theme.variants(name, prop, themes)"
      })
    }), "\n", _jsx(_components.p, {
      children: "It's often useful to create variants of the same component that are selected\nvia an additional prop."
    }), "\n", _jsxs(_components.p, {
      children: ["To make this easier with theming, styled-theming provides a\n", _jsx(_components.code, {
        children: "theme.variants"
      }), " function."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import styled from \"styled-components\";\nimport theme from \"styled-theming\";\n\nconst backgroundColor = theme.variants(\"mode\", \"variant\", {\n  default: { light: \"gray\", dark: \"darkgray\" },\n  primary: { light: \"blue\", dark: \"darkblue\" },\n  success: { light: \"green\", dark: \"darkgreen\" },\n  warning: { light: \"orange\", dark: \"darkorange\" },\n});\n\nconst Button = styled.button`\n  background-color: ${backgroundColor};\n`;\n\nButton.propTypes = {\n  variant: PropTypes.oneOf([\"default\", \"primary\", \"success\", \"warning\"])\n};\n\nButton.defaultProps = {\n  variant: \"default\",\n};\n\n<Button />\n<Button variant=\"primary\" />\n<Button variant=\"success\" />\n<Button variant=\"warning\" />\n"
      })
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
